import React, { useState, useEffect } from "react";
import { fetchAllAssets, deleteAssetById } from "../../services/assetService";
import { generateAssetQRCode } from "../../services/qrcodeService";
import { getFullImageUrl, getThumbnailUrl } from "../../utils/urlConfig";
import { toast } from 'react-toastify';
import AssetModal from "./AssetModal";
import styles from "./AssetsTable.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import ConfirmModal from "../Modal/ConfirmModal";
import ImageModal from "../Modal/ImageModal";
import AssetsTree from "./AssetsTree";
import { FaListUl, FaSitemap, FaFilter, FaTimes } from "react-icons/fa";
import TagService from "../../services/TagService";
import Select from "react-select";
import { FaCheck } from "react-icons/fa";
import QRCode from "react-qr-code";


const AssetsTable = () => {
  const [assets, setAssets] = useState([]); // All assets fetched from the API
  const [filteredAssets, setFilteredAssets] = useState([]); // Filtered and sorted assets for display
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [pageSize] = useState(5); // Items per page
  const [searchQuery, setSearchQuery] = useState(""); // Search term
  const [sortField, setSortField] = useState(""); // Field to sort by
  const [sortOrder, setSortOrder] = useState(""); // Sort order ('asc' or 'desc')
  // view asset details
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // delete asset
  const [selectedAssetDeleteId, setSelectedAssetDeleteId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // image modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState([]);
  const [viewMode, setViewMode] = useState("table"); // 'table' or 'tree'

  // Advanced Filtering State
  const [showFilters, setShowFilters] = useState(false);
  const [tagFilter, setTagFilter] = useState([]);
  const [statusFilters, setStatusFilters] = useState({
    insured: false,
    damaged: false,
    archived: false
  });
  const [availableTagsForFilter, setAvailableTagsForFilter] = useState([]);

  /**
   * Opens the modal to view asset details.
   * @param {number} id - The ID of the asset to view.
   */
  const openModal = (id) => {
    setSelectedAssetId(id);
    setIsModalOpen(true);
  };

  /**
   * Closes the asset details modal.
   */
  const closeModal = () => {
    setSelectedAssetId(null);
    setIsModalOpen(false);
  };

  /**
   * Fetches the list of assets and available tags from the API.
   */
  const fetchData = async () => {
    setLoading(true);
    try {
      const [assetsData, tagsData] = await Promise.all([
        fetchAllAssets(),
        TagService.getAllTags()
      ]);

      if (assetsData != null) {
        setAssets(assetsData);
        setFilteredAssets(assetsData);
      }
      
      if (tagsData != null) {
        setAvailableTagsForFilter(tagsData.map(t => ({ value: t.id, label: t.name, color: t.color })));
      }
    } catch (err) {
      setError(err.message);
      toast.error(`Failed to fetch data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Refreshes the list of assets (useful after updating an asset).
   */
  const refreshAssets = () => {
    fetchData();
  };

  /**
   * Filters and sorts the assets based on all criteria.
   */
  useEffect(() => {
    let result = [...assets];

    // 1. Filter by search query (Global Search)
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((asset) => {
        const matchName = asset.name?.toLowerCase().includes(lowerQuery);
        const matchDesc = asset.description?.toLowerCase().includes(lowerQuery);
        const matchId = asset.id?.toString().includes(lowerQuery);
        const matchManufacturer = asset.manufacturer?.toLowerCase().includes(lowerQuery);
        const matchModel = asset.modelNo?.toString().includes(lowerQuery);
        const matchSerial = asset.serialNo?.toString().includes(lowerQuery);
        const matchNotes = asset.notes?.toLowerCase()?.includes(lowerQuery);
        const matchQrToken = asset.qrToken?.toLowerCase()?.includes(lowerQuery);
        
        const matchTags = asset.tags?.some(tag => 
             tag.name?.toLowerCase().includes(lowerQuery)
        );

        return matchName || matchDesc || matchId || matchManufacturer || matchModel || matchSerial || matchNotes || matchQrToken || matchTags;
      });
    }

    // 2. Filter by Status pills
    if (statusFilters.insured) {
        result = result.filter(asset => asset.insured === 1);
    }
    if (statusFilters.damaged) {
        result = result.filter(asset => asset.damaged === 1);
    }
    if (statusFilters.archived) {
        result = result.filter(asset => asset.archived === 1);
    }

    // 3. Filter by Selected Tags
    if (tagFilter.length > 0) {
        const selectedTagIds = tagFilter.map(t => t.value);
        result = result.filter(asset => 
            asset.tags?.some(tag => selectedTagIds.includes(tag.id))
        );
    }

    // 4. Sort assets based on the selected field and order
    if (sortField) {
      result.sort((a, b) => {
        const aValue = a[sortField] || "";
        const bValue = b[sortField] || "";
        if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
        if (sortOrder === "desc") return aValue < bValue ? 1 : -1;
        return 0;
      });
    }

    setFilteredAssets(result);
  }, [searchQuery, sortField, sortOrder, assets, tagFilter, statusFilters]);

  /**
   * Fetches the asset list on component mount.
   */
  useEffect(() => {
    fetchData();
  }, []);


  /**
   * Paginates the filtered assets based on the current page and page size.
   */
  const paginatedAssets = filteredAssets.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );



  /**
   * Toggles a status filter.
   */
  const toggleStatusFilter = (key) => {
    setStatusFilters(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  /**
   * Clears all filters.
   */
  const clearFilters = () => {
    setSearchQuery("");
    setTagFilter([]);
    setStatusFilters({
      insured: false,
      damaged: false,
      archived: false
    });
  };

  /**
   * Handles sorting by toggling the sort order and updating the sort field.
   * @param {string} field - The field to sort by.
   */
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };


  /**
   * Updates the current page for pagination.
   * @param {number} page - The selected page number.
   */
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  /**
   * Checks if a given string is a valid URL.
   * @param {string} url - The URL string to validate.
   * @returns {boolean} - True if the string is a valid URL, otherwise false.
   */
  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };


  /**
   * Generates a QR code for a specific asset.
   * @param {number} assetId - The ID of the asset to generate a QR code for.
   */
  const handleGenerateClick = async (assetId) => {
    setLoading(true);
    try {
      console.log(assetId);
      await generateAssetQRCode(assetId);
      refreshAssets();
      toast.success("QR Code generated successfully");
    } catch (error) {
      console.error("Failed to generate QR code:", error);
      toast.error("Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  // Handles opening the delete confirmation modal for the selected asset
  const handleAssetDelete = async (assetId) => {
    setSelectedAssetDeleteId(assetId);
    setIsDeleteModalOpen(true);
  }

  // Closes the delete confirmation modal and resets the selected asset ID
  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedAssetDeleteId(null);
  };

  // Confirms the asset deletion by calling the delete function and refreshing the assets list
  const handleDeleteConfirm = async () => {
    try {
      await deleteAssetById(selectedAssetDeleteId);
      toast.success("Asset deleted successfully");
    } catch (error) {
      console.error("Failed to delete asset:", error);
      toast.error("Failed to delete asset");
    } finally {
      setLoading(false);
      refreshAssets();
    }
    setIsDeleteModalOpen(false);
  };

  // Open the modal and set selected images and current image index
  const handleImageClick = (images, index) => {
    setSelectedImages(images);
    setCurrentImageIndex(index);
    setIsImageModalOpen(true);
  };

  // Go to the next image in the list
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % selectedImages.length);
  };

  // Go to the previous image in the list
  const handlePreviousImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + selectedImages.length) % selectedImages.length
    );
  };

  // Close the modal
  const handleCloseModal = () => {
    setIsImageModalOpen(false);
  };

  /**
   * Renders table rows for the paginated assets.
   * @returns {JSX.Element[]} - The rows of the table.
   */
  const renderRows = () =>
    paginatedAssets.map((asset) => {
      const qrCodeUrl = asset.qrCode
        ? getFullImageUrl(asset.qrCode)
        : null;

      return (
        <tr key={asset.id}>
          <td data-label="ID">{asset.id}</td>
          <td data-label="Name">{asset.name}</td>
          <td data-label="Description">{asset.description}</td>
          <td data-label="Tags">
            <div className={styles.tag_container}>
                {asset.tags && asset.tags.length > 0 ? (
                    asset.tags.map(tag => (
                        <span 
                            key={tag.id}
                            className={styles.tag_badge}
                            style={{ 
                                backgroundColor: tag.color ? `${tag.color}22` : '#f1f5f9',
                                border: `1px solid ${tag.color || '#e2e8f0'}`,
                                color: tag.color || 'var(--text-main)',
                            }}
                            title={tag.name}
                        >
                            {tag.name}
                        </span>
                    ))
                ) : (
                    <span className={styles.tag_empty}>No tags</span>
                )}
            </div>
          </td>
          <td data-label="Image" className={styles.tdImg}>
            {asset.primaryImage ? (
              <img
                style={{ width: '50px', height: '50px', cursor: 'pointer', objectFit: 'cover', borderRadius: '4px' }}
                src={getThumbnailUrl(asset.primaryImage)}
                alt="Asset Thumbnail"
                loading="lazy"
                onClick={() => handleImageClick(asset.images, 0)}
              />
            ) : asset.images && asset.images.length > 0 ? (
              <img
                style={{ width: '50px', height: '50px', cursor: 'pointer', objectFit: 'cover', borderRadius: '4px' }}
                src={getThumbnailUrl(asset.images[0])}
                alt="Asset Thumbnail"
                loading="lazy"
                onClick={() => handleImageClick(asset.images, 0)}
              />
            ) : (
              <div style={{ width: "50px", height: "50px", background: "#eee", borderRadius: "4px" }}></div>
            )}
          </td>
          <td data-label="Details" className={styles.tdAssetInfo}>
            <button
              onClick={() => openModal(asset.id)}
              className={styles.assetDetailsButton}
            >
              <i className="fas fa-eye"></i>
            </button>
          </td>
          <td data-label="QR Code" className={styles.tdqr}>
            {asset.qrToken ? (
              <div style={{ background: 'white', padding: '4px', borderRadius: '6px', display: 'inline-block', border: '1px solid #e2e8f0' }}>
                <a className={styles.qrLink} href={`${window.location.origin}/scan/${asset.qrToken}`} title="Open QR scan page">
                  <QRCode 
                    value={`${window.location.origin}/scan/${asset.qrToken}`} 
                    size={44} 
                  />
                </a>
                <div style={{ fontSize: '9px', textAlign: 'center', marginTop: '2px', color: '#94a3b8', maxWidth: '56px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.qrToken}</div>
              </div>
            ) : qrCodeUrl && isValidUrl(qrCodeUrl) ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img
                  src={getFullImageUrl(asset.qrCode)}
                  alt="Asset QR Code"
                  style={{ width: "100px", height: "100px" }}
                />
                <a
                  href={getFullImageUrl(asset.qrCode)}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0, 0, 0, 0.5)",
                    color: "#fff",
                    textAlign: "center",
                    lineHeight: "100px",
                    opacity: 0,
                    transition: "opacity 0.3s",
                    textDecoration: "none",
                    fontWeight: "bold",
                  }}
                  onMouseOver={(e) => (e.target.style.opacity = 1)}
                  onMouseOut={(e) => (e.target.style.opacity = 0)}
                >
                  View
                </a>
              </div>
            ) : (
              <button
                className={`${styles.generateButton} ${loading ? styles.generateButtonLoading : ""}`}
                onClick={() => handleGenerateClick(asset.id)}
                disabled={loading}
              >
                {loading ? (
                  <span className={styles.spinner}></span>
                ) : (
                  "Generate"
                )}
              </button>
            )}
          </td>
          <td data-label="Delete">
            <button className={styles.deleteButton} onClick={() => handleAssetDelete(asset.id)}>
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </td>

          </tr>
      );
    });

  if (loading && assets.length === 0) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>Assets</h2>
      {/* Search and Toggle Row */}
      <div className={styles.toolbar}>
        <input
          type="text"
          placeholder="Search by name, description or tags"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          className={styles.searchInput}
        />

        <button 
          className={`${styles.filterToggleBtn} ${showFilters ? styles.activeFilterToggle : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> {showFilters ? "Hide Filters" : "Filter"}
          {(tagFilter.length > 0 || statusFilters.insured || statusFilters.damaged || statusFilters.archived) && (
            <span style={{ 
              background: 'var(--primary-color)', 
              color: 'white', 
              borderRadius: '50%', 
              width: '18px', 
              height: '18px', 
              fontSize: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginLeft: '4px'
            }}>
              {(tagFilter.length > 0 ? 1 : 0) + (statusFilters.insured ? 1 : 0) + (statusFilters.damaged ? 1 : 0) + (statusFilters.archived ? 1 : 0)}
            </span>
          )}
        </button>

        <div className={styles.viewToggle}>
          <button
            className={`${styles.toggleButton} ${viewMode === 'table' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('table')}
            title="Table View"
          >
            <FaListUl />
          </button>
          <button
            className={`${styles.toggleButton} ${viewMode === 'tree' ? styles.activeToggle : ''}`}
            onClick={() => setViewMode('tree')}
            title="Tree View"
          >
            <FaSitemap />
          </button>
        </div>
      </div>

      {/* Advanced Filter Bar */}
      {showFilters && (
        <div className={styles.filterBar}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Filter by Tags</span>
            <Select
              isMulti
              options={availableTagsForFilter}
              value={tagFilter}
              onChange={setTagFilter}
              placeholder="Select tags..."
              className="basic-multi-select"
              classNamePrefix="select"
              styles={{
                control: (base) => ({
                    ...base,
                    borderRadius: '8px',
                    borderColor: 'var(--border-color)',
                    minHeight: '40px'
                })
              }}
            />
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Status</span>
            <div className={styles.statusToggles}>
              <button 
                className={`${styles.statusPill} ${statusFilters.insured ? styles.activePill : ''}`}
                onClick={() => toggleStatusFilter('insured')}
              >
                {statusFilters.insured && <FaCheck size={10} />} Insured
              </button>
              <button 
                className={`${styles.statusPill} ${statusFilters.damaged ? styles.activePill : ''}`}
                onClick={() => toggleStatusFilter('damaged')}
              >
                {statusFilters.damaged && <FaCheck size={10} />} Damaged
              </button>
              <button 
                className={`${styles.statusPill} ${statusFilters.archived ? styles.activePill : ''}`}
                onClick={() => toggleStatusFilter('archived')}
              >
                {statusFilters.archived && <FaCheck size={10} />} Archived
              </button>
            </div>
          </div>

          <div className={styles.filterActions}>
            <button className={styles.clearFiltersBtn} onClick={clearFilters}>
              <FaTimes /> Clear All
            </button>
          </div>
        </div>
      )}

      {/* Conditional Rendering: Table or Tree */}
      {viewMode === "table" ? (
        <>
          <div className={styles["table-wrapper-div"]}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th onClick={() => handleSort("id")}>ID</th>
                  <th onClick={() => handleSort("name")}>Name</th>
                  <th onClick={() => handleSort("description")}>Description</th>
                  <th>Tags</th>
                  <th>Image</th>
                  <th>Details</th>
                  <th onClick={() => handleSort("")}>QR Code</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>{renderRows()}</tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className={styles.pagination}>
            {/* Back Button */}
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={styles.paginationButton}
            >
              &lt;
            </button>

            {/* Page Numbers */}
            {Array.from(
              { length: Math.ceil(filteredAssets.length / pageSize) },
              (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={
                    currentPage === i + 1
                      ? styles.activePage
                      : styles.paginationButton
                  }
                >
                  {i + 1}
                </button>
              )
            )}

            {/* Next Button */}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredAssets.length / pageSize)}
              className={styles.paginationButton}
            >
              &gt;
            </button>
          </div>
        </>
      ) : (
        <AssetsTree assets={filteredAssets} onViewDetail={openModal} />
      )}

      {/* Modal for Asset Details */}
      <AssetModal
        isOpen={isModalOpen}
        onClose={closeModal}
        assetId={selectedAssetId}
        refreshAssets={refreshAssets}
      />

      <ConfirmModal
        show={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this item?"
        details={"Asset ID : " + selectedAssetDeleteId}
      />

      {/* ImageModal */}
      <ImageModal
        isOpen={isImageModalOpen}
        images={selectedImages}
        currentImageIndex={currentImageIndex}
        onClose={handleCloseModal}
        onNext={handleNextImage}
        onPrevious={handlePreviousImage}
      />

    </div>
  );
};

export default AssetsTable;
